import * as React from 'react';
import { Modifier, usePopper } from 'react-popper';
import classNames from 'classnames';

import Portal from '../Portal';

import styles from './Overlay.module.scss';

type Placement =
  | 'auto-start'
  | 'auto'
  | 'auto-end'
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'right-start'
  | 'right'
  | 'right-end'
  | 'bottom-end'
  | 'bottom'
  | 'bottom-start'
  | 'left-end'
  | 'left'
  | 'left-start';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  wrapperId?: string;
  target: HTMLElement | null;
  show: boolean;
  children: React.ReactNode;
  onHide: () => void;
  // Overlay placement in relation to the target reference
  placement?: Placement;
  skidding?: number;
  offset?: number;
  tooltip?: boolean;
  // Overlay can be wider than 100vw
  canOverflow?: boolean;
  // Overlay will be forcely placed to the left with 100vw
  fullMode?: boolean;
  /** Whether the overlay's width should match its reference's width */
  matchReferenceSize?: boolean;
  /** Called when Popper updates its placement */
  onUpdatePlacement?: (value?: Placement) => void;
}

/**
 * <Overlay> component
 *
 * This component positions any component in relation to a target
 */
export const Overlay: React.FunctionComponent<Props> = ({
  className,
  wrapperId = 'default-overlay-id',
  style,
  target,
  show,
  children,
  onHide = () => null,
  // Overlay placement in relation to the target reference
  placement = 'bottom',
  skidding = 0,
  offset = 8,
  tooltip = false,
  canOverflow = false,
  fullMode = false,
  matchReferenceSize,
  onUpdatePlacement,
  ...htmlElementProps
}) => {
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(
    null,
  );

  const modifiers = React.useMemo(
    (): Modifier<string, Record<string, unknown>>[] => [
      {
        name: 'offset',
        options: {
          offset: [skidding, offset],
        },
      },
      {
        name: 'matchReferenceSize',
        enabled: !!matchReferenceSize,
        fn: ({ state }) => {
          const widthOrHeight =
            state.placement.startsWith('left') ||
            state.placement.startsWith('right')
              ? 'height'
              : 'width';

          if (!popperElement) return;

          const popperSize =
            popperElement[
              `offset${
                widthOrHeight[0].toUpperCase() + widthOrHeight.slice(1)
              }` as 'offsetWidth'
            ];
          const referenceSize = state.rects.reference[widthOrHeight];

          if (Math.round(popperSize) === Math.round(referenceSize)) return;

          popperElement.style[widthOrHeight] = `${referenceSize}px`;
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
    ],
    [matchReferenceSize, offset, popperElement, skidding],
  );

  const {
    styles: popperStyles,
    attributes,
    update,
  } = usePopper(target, popperElement, {
    placement,
    modifiers,
  });

  React.useEffect(() => {
    if (update) {
      update();
    }
  }, [show, update]);

  React.useEffect(() => {
    const resolvedPlacement = attributes?.popper?.['data-popper-placement'] as
      | Placement
      | undefined;
    // Called directly (not via queueMicrotask) so the consumer's placement
    // update runs inside this effect — batched in production and wrapped in
    // act() under test. Consumers only use the placement for a CSS class, so it
    // never feeds back into the popper (no re-render loop).
    onUpdatePlacement?.(resolvedPlacement);
  }, [attributes?.popper, onUpdatePlacement]);

  /**
   * Hide overlay if user clicks outside the overlay
   */
  React.useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        show &&
        event.target !== target &&
        !target?.contains(event.target as Node) &&
        !popperElement?.contains(event.target as Node)
      ) {
        onHide();
      }
    };
    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);
  });

  return (
    <Portal wrapperId={wrapperId}>
      <div
        ref={setPopperElement}
        className={classNames(styles.overlay, className, {
          show,
          tooltip,
          overflow: canOverflow,
          full: fullMode,
        })}
        style={{
          ...style,
          ...popperStyles.popper,
        }}
        role="dialog"
        aria-modal
        {...attributes.popper}
        {...htmlElementProps}
      >
        {children}
      </div>
    </Portal>
  );
};

export default Overlay;
